<template>
	<div class="chat-image-preview">
		<div class="post-image">
			<img v-if="image" :src="image" :alt="title" @click="handleViewImage" />
			<div v-else class="post-placeholder">
				<InlineSvg src="/images/icons/ic-image.svg" />
			</div>
		</div>
		<div class="action-buttons">
			<div class="action-button share-button" @click="onShare">
				<span>Share</span>
				<InlineSvg src="/images/icons-2025/share.svg" />
			</div>
			<div class="action-button download-button" @click="onDownload">
				<InlineSvg src="/images/icons-2025/download.svg" />
			</div>
		</div>
	</div>
</template>

<script>
	import { mapGetters } from 'vuex'
	import userflow from '~/plugins/userflow.js'
	import ImageActionsMixin from './mixins/ImageActionsMixin.js'

	export default {
		name: 'ChatImagePreview',
		mixins: [ImageActionsMixin],
		props: {
			image: {
				type: String,
				default: undefined,
			},
			title: {
				type: String,
				default: undefined,
			},
		},
		computed: {
			...mapGetters('Auth', ['user']),
		},
		mounted() {
			this.$stanAnalytics('chat-v2-marketing-post-displayed', {
				meta: { user_id: this.user?.user_id, username: this.user?.username },
				props: {
					image_src: this.image,
					image_title: this.title,
					trigger_location: 'chat-image-preview',
				},
			})
			userflow.TrackUserflowEvent('chat-v2-marketing-post-displayed', {
				image_src: this.image,
				image_title: this.title,
				trigger_location: 'chat-image-preview',
			})
		},
		methods: {
			onShare() {
				// Track share clicked event
				this.$stanAnalytics('chat-v2-marketing-post-share', {
					meta: { user_id: this.user?.user_id, username: this.user?.username },
					props: {
						image_src: this.image,
						image_title: this.title,
						trigger_location: 'chat-image-preview',
					},
				})
				this.$axios.post('/v1/users/onboarding_dashboard_marketing_post_created', {})

				this.handleShare(this.image, this.title, this.title || 'chat-image')
			},
			onDownload() {
				// Track download clicked event
				this.$stanAnalytics('chat-v2-marketing-post-download', {
					meta: { user_id: this.user?.user_id, username: this.user?.username },
					props: {
						image_src: this.image,
						image_title: this.title,
						trigger_location: 'chat-image-preview',
					},
				})
				this.$axios.post('/v1/users/onboarding_dashboard_marketing_post_created', {})

				this.handleDownload(this.image, this.title || 'chat-image')
			},
			handleViewImage() {
				this.$emit('view-image', {
					image: this.image,
					title: this.title,
				})
			},
		},
	}
</script>

<style lang="scss" scoped>
	.chat-image-preview {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: 12px;
		margin: 8px 0;
		max-width: 50%;

		.post-image {
			width: 100%;
			max-width: 300px;
			border-radius: 8px;
			overflow: hidden;
			background: #f3f4f6;

			img {
				width: 100%;
				height: auto;
				object-fit: cover;
				display: block;
			}

			.post-placeholder {
				width: 100%;
				height: 200px;
				display: flex;
				align-items: center;
				justify-content: center;
				color: #dae0ea;

				svg {
					width: 48px;
					height: 48px;
				}
			}
		}

		.action-buttons {
			display: flex;
			gap: 8px;
			width: 100%;
		}

		.action-button {
			background: #ebebff;
			color: #6355ff;
			border: none;
			font-family: 'Inter', sans-serif;
			font-weight: 500;
			font-size: 16px;
			line-height: 110%;
			cursor: pointer;
			transition: all 0.2s ease;
			display: flex;
			align-items: center;
			justify-content: center;
			gap: 4px;

			span {
				font-family: 'Inter', sans-serif;
				font-weight: 500;
				font-size: 16px;
				line-height: 110%;
			}
			svg::v-deep {
				width: 24px;
				height: 24px;

				path {
					fill: #6355ff;
				}
			}

			&:hover:not(:disabled) {
				background: #e0e0f5;
			}

			&:disabled {
				opacity: 0.6;
				cursor: not-allowed;
			}

			&.share-button {
				background: #6355ff;
				color: #ffffff;
				padding: 8px 20px 8px 24px;
				border-radius: 9999px;
				svg::v-deep {
					path {
						fill: #ffffff;
					}
				}
			}

			&.download-button {
				width: 48px;
				height: 48px;
				padding: 0;
				border-radius: 50%;
			}
		}
	}
</style>
